// frontend/src/pages/components/ExportJobFormDialog.tsx
import { useState, useEffect } from 'react';
import {
  Dialog, DialogTitle, DialogContent, DialogActions, Button,
  TextField, Grid,
  FormControlLabel, Switch, Typography,
  FormControl, InputLabel, Select, MenuItem,
  Checkbox, ListItemText
} from '@mui/material';
import { getApiUrl, authFetch } from '../../config';
import { QUELLE_LABELS, DATEN_LABELS, QUELLE_ORDER, DATEN_ORDER } from './exportJobLabels';

interface Props {
  open: boolean;
  record: any | null;
  onClose: () => void;
  onSave: () => void;
}

const PHONE_FORMAT_LABELS: Record<string, string> = {
  national: 'National (0…)',
  international: 'International',
  e164: 'E.164 (+49…)',
  digits: 'Nur Ziffern',
};

const HASH_ALGO_LABELS: Record<string, string> = {
  none: 'Keiner',
  md5: 'MD5',
  sha1: 'SHA-1',
  sha256: 'SHA-256',
};

// quelle kann aus dem GET als Array ODER als kommaseparierter String (mysql2-SET) kommen.
const normalizeQuelle = (raw: any): string[] => {
  if (Array.isArray(raw)) return raw;
  if (raw) return String(raw).split(',').filter(Boolean);
  return ['sperrliste'];
};

export default function ExportJobFormDialog({ open, record, onClose, onSave }: Props) {
  const isEdit = !!record?.id;

  const [form, setForm] = useState({
    name: '',
    active: true,
    quelle: ['sperrliste'] as string[],
    daten: 'telefon' as 'telefon' | 'email' | 'kunden' | 'kundenanschrift' | 'matchcode',
    filename_template: '',
    output_subdir: '',
    phone_format: 'national' as 'national' | 'international' | 'e164' | 'digits',
    hash_algo: 'none' as 'none' | 'md5' | 'sha1' | 'sha256',
    csv_delimiter: ';',
    csv_quote: '"',
    csv_header: true,
    csv_encoding: 'utf8' as 'utf8' | 'latin1',
    csv_eol: 'crlf' as 'crlf' | 'lf',
    zip_enabled: false,
    zip_method: 'aes256' as 'aes256',
    zip_password: '',
  });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (record) {
      setForm({
        name: record.name || '',
        // booleans tolerant: aus mysql2 als 0|1, sonst boolean
        active: record.active !== false && record.active !== 0,
        // quelle tolerant: Array oder kommaseparierter String aus mysql2-SET
        quelle: normalizeQuelle(record.quelle),
        daten: record.daten || 'telefon',
        filename_template: record.filename_template || '',
        output_subdir: record.output_subdir || '',
        phone_format: record.phone_format || 'national',
        hash_algo: record.hash_algo || 'none',
        csv_delimiter: record.csv_delimiter ?? ';',
        csv_quote: record.csv_quote ?? '"',
        csv_header: record.csv_header !== false && record.csv_header !== 0,
        csv_encoding: record.csv_encoding || 'utf8',
        csv_eol: record.csv_eol || 'crlf',
        zip_enabled: record.zip_enabled === true || record.zip_enabled === 1,
        zip_method: record.zip_method || 'aes256',
        // zip_password kommt nie aus GET -> Edit IMMER leer vorbelegen
        zip_password: '',
      });
    } else {
      setForm({
        name: '',
        active: true,
        quelle: ['sperrliste'],
        daten: 'telefon',
        filename_template: '',
        output_subdir: '',
        phone_format: 'national',
        hash_algo: 'none',
        csv_delimiter: ';',
        csv_quote: '"',
        csv_header: true,
        csv_encoding: 'utf8',
        csv_eol: 'crlf',
        zip_enabled: false,
        zip_method: 'aes256',
        zip_password: '',
      });
    }
    setError('');
  }, [record, open]);

  const handleSave = async () => {
    if (!form.name.trim()) {
      setError('Name ist Pflichtfeld');
      return;
    }
    if (!form.filename_template.trim()) {
      setError('Dateiname-Vorlage ist Pflichtfeld');
      return;
    }
    if (!form.quelle || form.quelle.length === 0) {
      setError('Mindestens eine Quelle ist Pflicht');
      return;
    }
    if (!form.daten) {
      setError('Daten ist Pflichtfeld');
      return;
    }
    setSaving(true);
    setError('');
    try {
      // Submit-Body: schreibbare Felder. zip_password NUR aufnehmen, wenn nicht leer.
      const body: Record<string, any> = {
        name: form.name.trim(),
        active: form.active,
        quelle: form.quelle,
        daten: form.daten,
        filename_template: form.filename_template.trim(),
        output_subdir: form.output_subdir,
        phone_format: form.phone_format,
        hash_algo: form.hash_algo,
        csv_delimiter: form.csv_delimiter,
        csv_quote: form.csv_quote,
        csv_header: form.csv_header,
        csv_encoding: form.csv_encoding,
        csv_eol: form.csv_eol,
        zip_enabled: form.zip_enabled,
        zip_method: form.zip_method,
      };
      if (form.zip_password.trim() !== '') {
        body.zip_password = form.zip_password;
      }

      const url = isEdit
        ? getApiUrl(`/sperrliste/export-jobs/${record.id}`)
        : getApiUrl('/sperrliste/export-jobs');
      const method = isEdit ? 'PUT' : 'POST';

      const res = await authFetch(url, {
        method,
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body)
      });
      const data = await res.json();
      if (!data.success) throw new Error(data.error || 'Speichern fehlgeschlagen');
      onSave();
    } catch (err) {
      setError((err as Error).message);
    }
    setSaving(false);
  };

  const set = (field: string) => (e: any) =>
    setForm(prev => ({ ...prev, [field]: e.target.value }));

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>{isEdit ? 'Export-Job bearbeiten' : 'Neuer Export-Job'}</DialogTitle>
      <DialogContent>
        {error && (
          <Typography color="error" sx={{ mb: 2 }}>{error}</Typography>
        )}
        <Grid container spacing={2} sx={{ mt: 0.5 }}>
          {/* Name + Aktiv */}
          <Grid size={{ xs: 12, sm: 9 }}>
            <TextField label="Name *" size="small" fullWidth value={form.name} onChange={set('name')} required />
          </Grid>
          <Grid size={{ xs: 12, sm: 3 }}>
            <FormControlLabel
              control={<Switch checked={form.active} onChange={(e) => setForm(prev => ({ ...prev, active: e.target.checked }))} />}
              label="Aktiv"
            />
          </Grid>

          {/* Quelle (Mehrfachauswahl) */}
          <Grid size={{ xs: 12, sm: 6 }}>
            <FormControl size="small" fullWidth>
              <InputLabel>Quelle *</InputLabel>
              <Select
                multiple
                value={form.quelle}
                label="Quelle *"
                renderValue={(selected) =>
                  (selected as string[]).map(q => QUELLE_LABELS[q] || q).join(', ')
                }
                onChange={(e) => {
                  const v = e.target.value;
                  setForm(prev => ({ ...prev, quelle: typeof v === 'string' ? v.split(',') : v }));
                }}
              >
                {QUELLE_ORDER.map(q => (
                  <MenuItem key={q} value={q}>
                    <Checkbox checked={form.quelle.includes(q)} />
                    <ListItemText primary={QUELLE_LABELS[q]} />
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>

          {/* Daten (Single-Select) */}
          <Grid size={{ xs: 12, sm: 6 }}>
            <FormControl size="small" fullWidth>
              <InputLabel>Daten *</InputLabel>
              <Select
                value={form.daten}
                label="Daten *"
                onChange={(e) => setForm(prev => ({ ...prev, daten: e.target.value as any }))}
              >
                {DATEN_ORDER.map(d => (
                  <MenuItem key={d} value={d}>{DATEN_LABELS[d]}</MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>

          {/* phone_format nur bei daten === telefon */}
          {form.daten === 'telefon' && (
            <Grid size={{ xs: 12, sm: 6 }}>
              <FormControl size="small" fullWidth>
                <InputLabel>Telefon-Format</InputLabel>
                <Select
                  value={form.phone_format}
                  label="Telefon-Format"
                  onChange={(e) => setForm(prev => ({ ...prev, phone_format: e.target.value as any }))}
                >
                  {(['national', 'international', 'e164', 'digits'] as const).map(p => (
                    <MenuItem key={p} value={p}>{PHONE_FORMAT_LABELS[p]}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
          )}

          {/* hash_algo nur bei daten in {telefon, email} (einspaltige Ausgaben) */}
          {(form.daten === 'telefon' || form.daten === 'email') && (
            <Grid size={{ xs: 12, sm: 6 }}>
              <FormControl size="small" fullWidth>
                <InputLabel>Hash-Verfahren</InputLabel>
                <Select
                  value={form.hash_algo}
                  label="Hash-Verfahren"
                  onChange={(e) => setForm(prev => ({ ...prev, hash_algo: e.target.value as any }))}
                >
                  {(['none', 'md5', 'sha1', 'sha256'] as const).map(h => (
                    <MenuItem key={h} value={h}>{HASH_ALGO_LABELS[h]}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
          )}

          {/* Dateiname-Vorlage + Ausgabe-Unterordner */}
          <Grid size={{ xs: 12, sm: 8 }}>
            <TextField
              label="Dateiname-Vorlage *"
              size="small"
              fullWidth
              value={form.filename_template}
              onChange={set('filename_template')}
              required
              helperText="Tokens: {YYYY} {MM} {DD} {HH} {mm} {ss} {JOBNAME}"
            />
          </Grid>
          <Grid size={{ xs: 12, sm: 4 }}>
            <TextField
              label="Ausgabe-Unterordner"
              size="small"
              fullWidth
              value={form.output_subdir}
              onChange={set('output_subdir')}
            />
          </Grid>

          {/* CSV-Optionen */}
          <Grid size={{ xs: 6, sm: 2 }}>
            <TextField label="Trennzeichen" size="small" fullWidth value={form.csv_delimiter} onChange={set('csv_delimiter')} />
          </Grid>
          <Grid size={{ xs: 6, sm: 2 }}>
            <TextField label="Quote" size="small" fullWidth value={form.csv_quote} onChange={set('csv_quote')} />
          </Grid>
          <Grid size={{ xs: 6, sm: 3 }}>
            <FormControl size="small" fullWidth>
              <InputLabel>Kodierung</InputLabel>
              <Select
                value={form.csv_encoding}
                label="Kodierung"
                onChange={(e) => setForm(prev => ({ ...prev, csv_encoding: e.target.value as any }))}
              >
                <MenuItem value="utf8">UTF-8</MenuItem>
                <MenuItem value="latin1">Latin-1</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid size={{ xs: 6, sm: 3 }}>
            <FormControl size="small" fullWidth>
              <InputLabel>Zeilenende</InputLabel>
              <Select
                value={form.csv_eol}
                label="Zeilenende"
                onChange={(e) => setForm(prev => ({ ...prev, csv_eol: e.target.value as any }))}
              >
                <MenuItem value="crlf">CRLF</MenuItem>
                <MenuItem value="lf">LF</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid size={{ xs: 12, sm: 2 }}>
            <FormControlLabel
              control={<Switch checked={form.csv_header} onChange={(e) => setForm(prev => ({ ...prev, csv_header: e.target.checked }))} />}
              label="Kopfzeile"
            />
          </Grid>

          {/* ZIP */}
          <Grid size={{ xs: 12, sm: 4 }}>
            <FormControlLabel
              control={<Switch checked={form.zip_enabled} onChange={(e) => setForm(prev => ({ ...prev, zip_enabled: e.target.checked }))} />}
              label="ZIP-Verschlüsselung (AES-256)"
            />
          </Grid>

          {/* zip_password nur wenn zip_enabled */}
          {form.zip_enabled && (
            <Grid size={{ xs: 12, sm: 8 }}>
              <TextField
                label="ZIP-Passwort"
                size="small"
                fullWidth
                type="password"
                value={form.zip_password}
                onChange={set('zip_password')}
                helperText="Beim Bearbeiten leer = unverändert"
              />
            </Grid>
          )}
        </Grid>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Abbrechen</Button>
        <Button variant="contained" onClick={handleSave} disabled={saving}>
          {saving ? 'Speichere...' : (isEdit ? 'Speichern' : 'Anlegen')}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
